import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  TOKEN_STORAGE_USER_KEY,
  VISUAL_TOKEN_STORAGE_KEY,
  LOGIN_STORAGE_USER,
} from 'auth/domain/User'
import { AnalyticsEvent } from 'core/analytics/events'
import useAnalytics from 'core/analytics/useAnalytics'
import { BASE_API_URL } from 'core/infra/environment'
import { HttpClient } from 'core/infra/http/HttpClient'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

/**
 * Get user data from storage
 */
function getUserData(storage: LocalStorageClient) {
  try {
    const userData = storage.get<string | { email: string; [key: string]: unknown }>(LOGIN_STORAGE_USER)
    if (!userData) return null

    let user: { email: string; [key: string]: unknown }
    if (typeof userData === 'string') {
      user = JSON.parse(userData)
    } else {
      user = userData
    }

    return {
      email: user.email,
    }
  } catch {
    return null
  }
}

/**
 * Hook to handle OpenDock authentication redirect.
 * 
 * When the backend redirects with ?opendock_auth=<temp_token>, the frontend:
 * 1. Extracts the temporary token from the query parameter
 * 2. Calls the backend exchange endpoint to get real tokens
 * 3. Stores tokens in frontend's expected format
 * 4. Authenticates the user
 * 5. Redirects to default authenticated route
 * 
 * Returns true if OpenDock auth is in progress (to show loading state)
 */
export function useOpendockAuthRedirect(): boolean {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const storage = new LocalStorageClient()
  const { track } = useAnalytics()
  const hasHandledAuth = useRef(false)
  const hasAuthenticatedRef = useRef(false)
  const abandonTrackedRef = useRef(false)
  const authTrackedRef = useRef(false)
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    // Only handle once
    if (hasHandledAuth.current) {
      return
    }

    const tempToken = searchParams.get('opendock_auth')

    // Check if we have a temporary token (not just "true")
    if (!tempToken || tempToken === 'true') {
      return
    }

    // Mark as handled to prevent re-processing
    hasHandledAuth.current = true
    setIsProcessing(true)

    // Track authentication start
    const userData = getUserData(storage)
    track(AnalyticsEvent.Authentication, { 
      authFlow: 'OpenDock', 
      authMethod: 'OpendockRedirect', 
      authStage: 'start',
      ...(userData ? { email: userData.email } : {})
    })

    console.log('[OpenDock Auth] Temporary token detected, exchanging for real tokens...', {
      tempTokenLength: tempToken.length,
      url: window.location.href,
    })

    // Exchange temporary token for real tokens
    exchangeOpendockToken(tempToken, navigate, storage, searchParams, track, hasAuthenticatedRef, authTrackedRef)
      .finally(() => {
        setIsProcessing(false)
      })
  }, [searchParams, navigate, storage, track])

  // Track abandon if component unmounts or user navigates away without successful authentication
  useEffect(() => {
    const handleAbandon = () => {
      if (hasHandledAuth.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
        abandonTrackedRef.current = true
        const userData = getUserData(storage)
        track(AnalyticsEvent.Authentication, { 
          authFlow: 'OpenDock', 
          authMethod: 'OpendockRedirect', 
          authStage: 'abandon',
          ...(userData ? { email: userData.email } : {})
        })
      }
    }

    // Track on page visibility change (user switches tabs/windows)
    const handleVisibilityChange = () => {
      if (document.hidden && hasHandledAuth.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
        abandonTrackedRef.current = true
        const userData = getUserData(storage)
        track(AnalyticsEvent.Authentication, { 
          authFlow: 'OpenDock', 
          authMethod: 'OpendockRedirect', 
          authStage: 'abandon',
          ...(userData ? { email: userData.email } : {})
        })
      }
    }

    // Track on page unload (user navigates away/closes tab)
    const handleBeforeUnload = () => {
      if (hasHandledAuth.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
        abandonTrackedRef.current = true
        const userData = getUserData(storage)
        track(AnalyticsEvent.Authentication, { 
          authFlow: 'OpenDock', 
          authMethod: 'OpendockRedirect', 
          authStage: 'abandon',
          ...(userData ? { email: userData.email } : {})
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      handleAbandon()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [track])

  return isProcessing
}

/**
 * Exchange temporary token for real authentication tokens
 */
async function exchangeOpendockToken(
  tempToken: string,
  navigate: ReturnType<typeof useNavigate>,
  storage: LocalStorageClient,
  searchParams: URLSearchParams,
  track: (event: AnalyticsEvent, properties?: Record<string, unknown>) => void,
  hasAuthenticatedRef: React.MutableRefObject<boolean>,
  authTrackedRef: React.MutableRefObject<boolean>
) {
  try {
    const httpClient = new HttpClient(BASE_API_URL)
    
    // Call the backend exchange endpoint
    const response = await httpClient.get<{
      portal_token: string
      visual_token?: string
      user_data: {
        email: string
        first_name: string
        last_name: string
        client: string
        issuer: string
      }
    }>(`/api/credentials/opendock/exchange?opendock_auth=${encodeURIComponent(tempToken)}`)

    const { portal_token, visual_token, user_data } = response.data

    console.log('[OpenDock Auth] Token exchange successful', {
      hasPortalToken: !!portal_token,
      hasVisualToken: !!visual_token,
      hasUserData: !!user_data,
    })

    // Store tokens in frontend's expected format
    storage.set(TOKEN_STORAGE_USER_KEY, portal_token)
    console.log('[OpenDock Auth] Stored portal token')

    if (visual_token) {
      storage.set(VISUAL_TOKEN_STORAGE_KEY, visual_token)
      console.log('[OpenDock Auth] Stored visual token')
    }

    // Store user data as JSON string (matching the pattern from backend responses)
    storage.set(LOGIN_STORAGE_USER, JSON.stringify(user_data))
    console.log('[OpenDock Auth] Stored user data:', user_data)

    // Verify tokens were set correctly
    const verifyToken = storage.get(TOKEN_STORAGE_USER_KEY)
    const verifyUser = storage.get(LOGIN_STORAGE_USER)
    console.log('[OpenDock Auth] Verification:', {
      tokenSet: !!verifyToken,
      userSet: !!verifyUser,
    })

    // Mark as authenticated
    hasAuthenticatedRef.current = true

    // Track authentication event (only once)
    if (!authTrackedRef.current) {
      authTrackedRef.current = true
      const userData = getUserData(storage)
      track(AnalyticsEvent.Authentication, { 
        authFlow: 'OpenDock', 
        authMethod: 'OpendockRedirect', 
        authStage: 'success',
        ...(userData ? { email: userData.email } : {})
      })
    }

    // Remove query parameter from URL
    const newUrl = window.location.pathname
    window.history.replaceState({}, '', newUrl)

    // Get redirect_to from query params or default to /demo
    const redirectTo = searchParams.get('redirect_to') || '/demo'
    console.log('[OpenDock Auth] Redirecting to:', redirectTo)

    // Use setTimeout to ensure localStorage writes are complete before redirect
    // This prevents race conditions with useSession() hook
    setTimeout(() => {
      navigate(redirectTo, { replace: true })
    }, 0)
  } catch (error: any) {
    console.error('[OpenDock Auth] Token exchange failed:', error)
    
    // Handle different error types
    let errorMessage = 'Authentication failed. Please try again.'
    
    if (error?.response?.status === 401) {
      errorMessage = 'Invalid or expired token. Please restart the authentication flow.'
    } else if (error?.response?.status === 400) {
      errorMessage = error?.response?.data?.detail || 'Invalid request. Please try again.'
    } else if (error?.code === 'ERR_NETWORK' || error?.message?.includes('CORS')) {
      errorMessage = 'Network error: Unable to connect to the server. Please check if the backend is running.'
    }

    // Track authentication failure
    const userData = getUserData(storage)
    track(AnalyticsEvent.Authentication, { 
      authFlow: 'OpenDock',
      authMethod: 'OpendockRedirect',
      authStage: 'failure',
      error: errorMessage,
      ...(userData ? { email: userData.email } : {})
    })

    // Remove query parameter from URL
    const newUrl = window.location.pathname
    window.history.replaceState({}, '', newUrl)

    // Redirect to home page on error
    // TODO: Consider showing an error message to the user
    console.error('[OpenDock Auth] Error details:', errorMessage)
    navigate('/', { replace: true })
  }
}
